import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Monitor, Search, ShieldCheck, UserCheck, UserX, Users } from "lucide-react";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import TrustedDevicesDialog from "@/components/TrustedDevicesDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const GOLD_INK = "var(--brand-accent-ink)";

export default function GlobalUmsPanel({ tenants }) {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [tenant, setTenant] = useState("all");
  const [busy, setBusy] = useState(null); // user id being acted on
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [deviceTarget, setDeviceTarget] = useState(null);

  const load = () => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (tenant !== "all") params.tenant = tenant;
    platformApi.get("/ums/users", { params }).then((r) => setUsers(r.data.users));
  };

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [search, tenant]); // eslint-disable-line

  const act = async (u, action) => {
    setBusy(u.id);
    try {
      const { data } = await platformApi.post(`/ums/users/${u.id}/${action}`);
      toast.success(action === "deactivate"
        ? `Deactivated ${data.email}` : `Reactivated ${data.email}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const resetPassword = async () => {
    setResetting(true);
    try {
      const { data } = await platformApi.post(
        `/ums/users/${resetTarget.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${data.email}`);
      setResetTarget(null); setNewPassword("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="global-ums-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#1E293B] flex items-center gap-2">
          <Users className="h-4 w-4" style={{ color: GOLD_INK }} /> Global UMS
          <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-600">
            root only — every account, every tenant
          </span>
        </h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="ums-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or name…" className="rounded-none h-8 pl-8 w-56 text-[12px]" />
          </div>
          <Select value={tenant} onValueChange={setTenant}>
            <SelectTrigger className="rounded-none h-8 w-44 text-[11px]" data-testid="ums-tenant-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all" className="text-[12px]">All workspaces</SelectItem>
              <SelectItem value="platform" className="text-[12px]" data-testid="ums-tenant-platform">platform</SelectItem>
              {tenants.map((t) => (
                <SelectItem key={t.subdomain} value={t.subdomain} className="text-[12px]"
                  data-testid={`ums-tenant-${t.subdomain}`}>{t.subdomain}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!users ? <Skeleton className="h-48 w-full rounded-none" /> : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-[12px]" data-testid="ums-users-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50">
                {["Email", "Name", "Workspace", "Role", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-2.5 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.id} data-testid={`ums-row-${u.email}`}>
                  <td className="px-4 py-2.5 font-mono font-semibold text-[#1E293B]">{u.email}</td>
                  <td className="px-4 py-2.5 text-slate-700">{u.name || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-slate-500">{u.tenant_id}</td>
                  <td className="px-4 py-2.5 text-slate-500">{u.role}</td>
                  <td className="px-4 py-2.5">
                    {u.active !== false
                      ? <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-teal-50 border-teal-200 text-teal-700">active</span>
                      : <span className="text-[9px] font-mono uppercase px-1.5 py-0.5 border bg-red-50 border-red-200 text-red-700">deactivated</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    {u.protected ? (
                      <span className="text-[10px] font-mono uppercase text-slate-400 flex items-center gap-1"
                        data-testid={`ums-protected-${u.email}`}>
                        <ShieldCheck className="h-3.5 w-3.5" style={{ color: GOLD_INK }} /> protected
                      </span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <button data-testid={`ums-reset-${u.email}`}
                          onClick={() => { setResetTarget(u); setNewPassword(""); }}
                          className="text-[10px] font-mono border border-slate-300 px-2 py-1 hover:bg-slate-50 flex items-center gap-1">
                          <KeyRound className="h-3 w-3" /> reset pwd
                        </button>
                        <button data-testid={`ums-devices-${u.email}`}
                          onClick={() => setDeviceTarget(u)}
                          className="text-[10px] font-mono border border-slate-300 px-2 py-1 hover:bg-slate-50 flex items-center gap-1">
                          <Monitor className="h-3 w-3" /> devices
                        </button>
                        {u.active !== false ? (
                          <button data-testid={`ums-deactivate-${u.email}`} disabled={busy === u.id}
                            onClick={() => act(u, "deactivate")}
                            className="text-[10px] font-mono border border-slate-300 px-2 py-1 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 flex items-center gap-1">
                            {busy === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserX className="h-3 w-3" />} deactivate
                          </button>
                        ) : (
                          <button data-testid={`ums-reactivate-${u.email}`} disabled={busy === u.id}
                            onClick={() => act(u, "reactivate")}
                            className="text-[10px] font-mono border border-slate-300 px-2 py-1 hover:bg-teal-50 hover:text-teal-700 disabled:opacity-50 flex items-center gap-1">
                            {busy === u.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <UserCheck className="h-3 w-3" />} reactivate
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-400" data-testid="ums-empty">
                  No accounts match this filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) setResetTarget(null); }}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="ums-reset-dialog">
          <DialogHeader>
            <DialogTitle className="text-[14px] font-bold text-[#1E293B]">
              Reset password — {resetTarget?.email}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-slate-500 -mt-1">
            Workspace <span className="font-mono">{resetTarget?.tenant_id}</span>. The user signs in with the new
            password immediately. This action is written to the Observatory audit feed.
          </p>
          <PasswordInput data-testid="ums-reset-password-input" value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="New password (min 8 characters)" className="rounded-none" autoComplete="new-password" />
          <DialogFooter>
            <Button data-testid="ums-reset-confirm-btn" onClick={resetPassword}
              disabled={resetting || newPassword.length < 8}
              className="rounded-none bg-[#1E293B] hover:bg-slate-700 text-white">
              {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Reset password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {deviceTarget && (
        <TrustedDevicesDialog open={!!deviceTarget} onOpenChange={(o) => { if (!o) setDeviceTarget(null); }}
          client={platformApi} subjectEmail={deviceTarget.email}
          title="Trusted devices"
          listPath={`/ums/users/${deviceTarget.id}/devices`}
          revokePath={(did) => `/ums/users/${deviceTarget.id}/devices/${did}/revoke`}
          revokeAllPath={`/ums/users/${deviceTarget.id}/devices/revoke-all`} />
      )}
    </div>
  );
}
